import React, { useCallback } from 'react'
import styles from './application-flow-submit-block.module.scss'

export interface ApplicationFlowProtocolItem {

  /** 协议的唯一标识 key */
  key: string

  /** 协议的展示名称 */
  name: string
}

export interface ApplicationFlowSubmitBadge {

  /** 按钮右上角展示的提示文案，仅用于当前提交状态的视觉强调。 */
  text: string

  /** 角标配色由皮肤决定；默认使用警示红，深色仅供橙色主题使用。 */
  tone?: 'default' | 'dark'
}

export interface ApplicationFlowSubmitBlockProps {

  /** 协议勾选按钮的 aria-label */
  agreementAriaLabel?: string

  /** 协议是否已勾选 */
  agreementChecked: boolean

  /** 自定义协议节点（优先级高于 agreements） */
  agreementContent?: React.ReactNode

  /** 自定义外层 class */
  className?: string

  /** 紧随按钮主文案展示的固定宽度倒计时。 */
  countdownText?: string

  /** 提交按钮是否禁用 */
  disabled?: boolean

  /** 协议与按钮的排列顺序：'agreement-first' (协议在上) 或 'button-first' (按钮在上) */
  order?: 'agreement-first' | 'button-first'

  /** 协议点击回调 */
  onOpenAgreement?: (key: string) => void

  /** 点击提交按钮时的回调 */
  onSubmit: () => void

  /** 切换协议勾选状态的回调 */
  onToggleAgreement: () => void

  /** 协议列表项 */
  agreements?: readonly ApplicationFlowProtocolItem[]

  /** 提交按钮显示的文案，如“去借钱”、“立即提现”、“提交中” */
  submitLabel: string

  /** 紧贴提交按钮右上角的非交互角标；不传时不占用页面空间。 */
  submitBadge?: ApplicationFlowSubmitBadge

  /** 展现模式：'fixed-footer' (页面底部固定栏) 或 'inline' (内联容器) */
  variant?: 'fixed-footer' | 'inline'
}

/**
 * ApplicationFlowSubmitBlock — 申请流程新皮肤通用提交与协议确认区块组件
 * @returns {React.ReactElement} 提交与协议确认区块
 */
const ApplicationFlowSubmitBlock: React.FC<ApplicationFlowSubmitBlockProps> = React.memo(({
  agreementAriaLabel = '切换协议同意状态',
  agreementChecked,
  agreementContent,
  agreements,
  className = '',
  countdownText = '',
  disabled = false,
  onOpenAgreement,
  onSubmit,
  onToggleAgreement,
  order = 'agreement-first',
  submitBadge,
  submitLabel,
  variant = 'fixed-footer',
}) => {
  const handleTextClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement

    if (target.closest('a, button')) {
      return
    }
    onToggleAgreement()
  }, [onToggleAgreement])

  let contentNode: React.ReactNode = agreementContent

  if (!contentNode) {
    if (Array.isArray(agreements) && agreements.length > 0) {
      contentNode = (
        <>
          <span>我已阅读并同意</span>
          {agreements.map((agreement, index) => (
            <React.Fragment key={agreement.key || agreement.name}>
              {index > 0 ? '、' : ''}
              <button
                onClick={() => onOpenAgreement?.(agreement.key)}
                type="button"
              >
                《{agreement.name}》
              </button>
            </React.Fragment>
          ))}
        </>
      )
    }
  }

  const isFixed = variant === 'fixed-footer'
  const ContainerTag = isFixed ? 'footer' : 'div'
  const variantClass = isFixed ? styles.fixedFooter : styles.inline
  const orderClass = order === 'button-first' ? styles.buttonFirst : styles.agreementFirst
  const containerClassName = `${styles.container} ${variantClass} ${orderClass} ${className}`.trim()

  return (
    <ContainerTag className={containerClassName}>
      <div className={styles.agreementRow}>
        <button
          aria-label={agreementAriaLabel}
          aria-pressed={agreementChecked}
          className={styles.checkboxButton}
          onClick={onToggleAgreement}
          type="button"
        >
          <span className={agreementChecked ? styles.checkboxChecked : styles.checkboxUnchecked}>
            {agreementChecked ? '✓' : ''}
          </span>
        </button>
        <div className={styles.agreementText} onClick={handleTextClick}>
          {contentNode}
        </div>
      </div>
      <div className={styles.submitArea}>
        {submitBadge?.text ? (
          <span
            aria-hidden="true"
            className={`${styles.submitBadge} ${submitBadge.tone === 'dark' ? styles.submitBadgeDark : ''}`.trim()}
          >
            {submitBadge.text}
          </span>
        ) : null}
        <button className={styles.submitButton} disabled={disabled} onClick={onSubmit} type="button">
          <span>{submitLabel}</span>
          {countdownText ? <span className={styles.countdown}>{countdownText}</span> : null}
        </button>
      </div>
    </ContainerTag>
  )
})

ApplicationFlowSubmitBlock.displayName = 'ApplicationFlowSubmitBlock'

export default ApplicationFlowSubmitBlock
